import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Crown, Sparkles, Camera, Infinity as InfinityIcon, Check, ArrowRight, ArrowLeft,
  Loader2, CheckCircle2, Lock, ShieldCheck, Utensils, Gift, CreditCard, Timer,
} from 'lucide-react';
import { useClientAuth } from '../context/ClientAuthContext';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { toast } from 'sonner';

const FEATURES = [
  { icon: Sparkles, fr: 'Recherche intelligente AI illimitée', en: 'Unlimited AI search' },
  { icon: Camera, fr: 'Reconnaissance de plats par photo', en: 'Photo meal recognition' },
  { icon: InfinityIcon, fr: 'Historique illimité de votre journal', en: 'Unlimited journal history' },
  { icon: Utensils, fr: 'Analyse santé détaillée de vos repas', en: 'Detailed health analysis' },
];

const SuiviProPage = () => {
  const { language } = useLanguage();
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);
  const { isAuthed, loading, authFetch } = useClientAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState('');
  const [poll, setPoll] = useState(null); // 'checking' | 'success' | 'error'
  const [promo, setPromo] = useState('');
  const [promoErr, setPromoErr] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [trialUsed, setTrialUsed] = useState(false);
  const [alreadyPremium, setAlreadyPremium] = useState(null); // null=inconnu, sinon {plan}

  const api = useCallback(
    (path, opts) => authFetch(path, opts).then((r) => (r.ok ? r.json() : Promise.reject(r))),
    [authFetch]
  );

  // Tarifs live (reflète automatiquement la promo -38% jusqu'au 31 août)
  useEffect(() => {
    api('/api/tracking/subscription/pricing').then(setPricing).catch(() => setPricing(null));
  }, [api]);

  // Statut d'éligibilité à l'essai gratuit + Suivi Pro déjà inclus (Communauté/Transformation/VIP)
  useEffect(() => {
    if (!isAuthed) return;
    api('/api/tracking/subscription').then((d) => {
      setTrialUsed(!!d.trial_used);
      if (d.premium && !['monthly', 'yearly'].includes(d.plan)) setAlreadyPremium({ plan: d.plan });
    }).catch(() => {});
  }, [isAuthed, api]);

  // Retour de Stripe → polling du statut
  const sessionId = params.get('sub_session_id');
  useEffect(() => {
    if (!sessionId || !isAuthed) return;
    setPoll('checking');
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const d = await api(`/api/tracking/subscription/status/${sessionId}`);
        if (d.payment_status === 'paid' || d.premium) { setPoll('success'); return; }
        if (d.status === 'expired' || attempts >= 6) { setPoll('error'); return; }
      } catch {
        if (attempts >= 6) { setPoll('error'); return; }
      }
      setTimeout(tick, 2000);
    };
    tick();
  }, [sessionId, isAuthed, api]);

  const subscribe = async (plan) => {
    if (!isAuthed) {
      navigate(`/espace-client?next=${encodeURIComponent(`/suivi-pro?autocheckout=${plan}`)}`);
      return;
    }
    setBusy(plan);
    try {
      const d = await api('/api/tracking/subscription/checkout', {
        method: 'POST',
        body: JSON.stringify({ plan, origin_url: window.location.origin }),
      });
      if (d.url) window.location.href = d.url;
    } catch {
      toast.error(L('Erreur lors de la création du paiement. Réessayez.', 'Error creating the payment session. Please retry.'));
      setBusy('');
    }
  };

  // Reprise automatique après inscription/connexion : si le client a cliqué "S'abonner"
  // sans compte, on relance directement le paiement pour l'offre choisie, sans clic
  // supplémentaire de sa part.
  const autoRef = React.useRef(false);
  useEffect(() => {
    const autoPlan = params.get('autocheckout');
    if (!autoPlan || !isAuthed || autoRef.current) return;
    autoRef.current = true;
    params.delete('autocheckout');
    setParams(params, { replace: true });
    subscribe(autoPlan);
  }, [isAuthed, params]);

  const redeemPromo = async () => {
    if (!isAuthed) { navigate('/espace-client'); return; }
    if (!promo.trim()) return;
    setPromoErr('');
    setPromoBusy(true);
    try {
      const d = await api('/api/tracking/subscription/redeem-code', {
        method: 'POST',
        body: JSON.stringify({ code: promo.trim() }),
      });
      if (d.premium) setPoll('success');
      else setPromoErr(L('Code invalide', 'Invalid code'));
    } catch {
      setPromoErr(L('Code promo invalide ou expiré', 'Invalid or expired promo code'));
    } finally {
      setPromoBusy(false);
    }
  };

  const PLAN_NAMES = { communaute: L('Communauté', 'Community'), transformation: L('100% en ligne', '100% Online'), vip: L('Hybride VIP', 'VIP Hybrid') };

  if (poll === 'success') {
    return (
      <main className="min-h-screen bg-black flex items-center justify-center px-4 pt-32 pb-16">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md" data-testid="sub-success">
          <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h1 className="text-3xl font-extrabold text-white">{L('Bienvenue dans Suivi Pro 🎉', 'Welcome to Suivi Pro 🎉')}</h1>
          <p className="text-zinc-400 mt-2">{L('AI illimitée et reconnaissance photo sont activées.', 'Unlimited AI and photo recognition are now active.')}</p>
          <button onClick={() => navigate('/mon-espace')} className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] transition-transform" data-testid="sub-success-go">
            {L('Accéder à mon suivi', 'Go to my tracking')} <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </main>
    );
  }

  if (alreadyPremium) {
    return (
      <main className="min-h-screen bg-black flex items-center justify-center px-4 pt-32 pb-16">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md" data-testid="sub-already-included">
          <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-3xl font-extrabold text-white">{L('Suivi Pro est déjà inclus !', 'Suivi Pro is already included!')}</h1>
          <p className="text-zinc-400 mt-2">
            {L(`Votre formule ${PLAN_NAMES[alreadyPremium.plan] || alreadyPremium.plan} inclut Suivi Pro (IA illimitée, reconnaissance photo) — aucun paiement supplémentaire nécessaire.`,
               `Your ${PLAN_NAMES[alreadyPremium.plan] || alreadyPremium.plan} plan includes Suivi Pro (unlimited AI, photo recognition) — no extra payment needed.`)}
          </p>
          <button onClick={() => navigate('/mon-espace')} className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] transition-transform" data-testid="sub-already-included-go">
            {L('Accéder à mon suivi', 'Go to my tracking')} <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </main>
    );
  }

  return (
    <>
      <SEOHead title={L('Suivi Pro | Body-Up', 'Suivi Pro | Body-Up')} description={L('Suivi nutritionnel premium : AI illimitée et reconnaissance photo.', 'Premium nutrition tracking: unlimited AI and photo recognition.')} path="/suivi-pro" />
      <main className="min-h-screen bg-black pt-24 sm:pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {isAuthed && (
            <button
              onClick={() => navigate('/mon-espace')}
              className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-zinc-400 hover:text-white transition-colors"
              data-testid="back-to-app-btn"
            >
              <ArrowLeft className="w-4 h-4" /> {L('Retour à mon espace', 'Back to my space')}
            </button>
          )}
          <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-yellow-500/20 to-primary/20 rounded-full border border-yellow-500/30 mb-4">
              <Crown className="w-4 h-4 text-yellow-400" />
              <span className="text-xs font-bold text-white tracking-wider">SUIVI PRO</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white">
              {L('Passez à la vitesse', 'Go to the next')}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-500 to-yellow-400">{L('supérieure', 'level')}</span>
            </h1>
            <p className="text-zinc-400 mt-4 max-w-xl mx-auto">{L('Le journal reste gratuit. Suivi Pro débloque l\'AI illimitée et la reconnaissance de vos plats par photo.', 'The journal stays free. Suivi Pro unlocks unlimited AI and photo meal recognition.')}</p>
          </motion.div>

          {poll === 'checking' && (
            <div className="mb-8 rounded-2xl border border-primary/30 bg-primary/10 p-5 flex items-center justify-center gap-3" data-testid="sub-checking">
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
              <span className="text-white text-sm font-semibold">{L('Vérification de votre paiement…', 'Verifying your payment…')}</span>
            </div>
          )}
          {poll === 'error' && (
            <div className="mb-8 rounded-2xl border border-orange-500/30 bg-orange-500/10 p-5 text-center text-sm text-orange-300" data-testid="sub-error">
              {L('Paiement non confirmé. Si vous avez payé, rechargez la page dans un instant.', 'Payment not confirmed. If you paid, refresh in a moment.')}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-5 mb-10 max-w-md sm:max-w-none mx-auto">
            {FEATURES.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 rounded-2xl bg-zinc-900/60 border border-white/10 p-4" data-testid={`feature-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/25 to-yellow-500/20 flex items-center justify-center shrink-0">
                  <f.icon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm font-semibold text-white">{en ? f.en : f.fr}</span>
              </motion.div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
            {/* Carte Mensuel — essai gratuit 7 jours */}
            <div className="relative rounded-3xl p-7 border border-white/10 bg-gradient-to-br from-zinc-900 to-black" data-testid="plan-monthly">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider text-white bg-gradient-to-r from-emerald-500 to-teal-400 whitespace-nowrap inline-flex items-center gap-1">
                <Gift className="w-3 h-3" /> {L('7 JOURS GRATUITS', '7 DAYS FREE')}
              </span>
              <div className="flex items-end gap-1.5 mb-1 mt-1">
                <span className="text-4xl font-extrabold text-white">{pricing ? pricing.monthly.price.toFixed(2) : '9.90'}</span>
                <span className="text-zinc-400 mb-1.5 font-semibold">CHF {L('/ mois', '/ month')}</span>
              </div>
              <p className="text-sm text-emerald-400 font-semibold mb-1 flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5" />
                {trialUsed
                  ? L('Facturation immédiate (essai déjà utilisé)', 'Billed immediately (trial already used)')
                  : L('Essai gratuit, carte requise à l\'inscription', 'Free trial, card required to sign up')}
              </p>
              <p className="text-xs text-zinc-500 mb-5">{L('Sans engagement · Annulable à tout moment', 'No commitment · Cancel anytime')}</p>
              <ul className="space-y-2.5 mb-6">
                {FEATURES.map((f) => (
                  <li key={f.fr} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-primary" /> {en ? f.en : f.fr}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => subscribe('monthly')} disabled={busy === 'monthly' || loading}
                className="w-full py-3.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="subscribe-monthly"
              >
                {busy === 'monthly' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                {!isAuthed ? L('Se connecter pour continuer', 'Log in to continue')
                  : trialUsed ? L('Choisir cette offre', 'Choose this plan')
                  : L('Démarrer mon essai gratuit', 'Start my free trial')}
              </button>
              <p className="text-[11px] text-zinc-600 text-center mt-3">
                {trialUsed
                  ? L('9.90 CHF/mois, débité chaque mois jusqu\'à annulation.', '9.90 CHF/month, billed monthly until cancelled.')
                  : L('Puis 9.90 CHF/mois si vous n\'annulez pas avant la fin de l\'essai.', 'Then 9.90 CHF/month if you don\'t cancel before the trial ends.')}
              </p>
            </div>

            {/* Carte Annuel — promo -38% jusqu'au 31 août */}
            <div className="relative rounded-3xl p-7 border border-yellow-500/50 bg-gradient-to-br from-yellow-500/8 to-black" data-testid="plan-yearly">
              {pricing?.yearly?.promo_active && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider text-black bg-gradient-to-r from-yellow-400 to-primary whitespace-nowrap inline-flex items-center gap-1">
                  <Timer className="w-3 h-3" /> -{pricing.yearly.discount_pct}% {L("JUSQU'AU 31 AOÛT", 'UNTIL AUG 31')}
                </span>
              )}
              <div className="flex items-end gap-1.5 mb-1 mt-1">
                {pricing?.yearly?.promo_active && (
                  <span className="text-lg text-zinc-500 line-through mb-1.5">{pricing.yearly.original_price.toFixed(0)}.-</span>
                )}
                <span className="text-4xl font-extrabold text-white">{pricing ? (pricing.yearly.price % 1 === 0 ? `${pricing.yearly.price.toFixed(0)}.-` : pricing.yearly.price.toFixed(2)) : '79.-'}</span>
                <span className="text-zinc-400 mb-1.5 font-semibold">CHF {L('/ an', '/ year')}</span>
              </div>
              <p className="text-sm text-primary font-semibold mb-5">{L('Paiement unique, sans renouvellement automatique', 'One-time payment, no auto-renewal')}</p>
              <ul className="space-y-2.5 mb-6">
                {FEATURES.map((f) => (
                  <li key={f.fr} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-yellow-400" /> {en ? f.en : f.fr}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => subscribe('yearly')} disabled={busy === 'yearly' || loading}
                className="w-full py-3.5 rounded-xl font-bold text-black bg-gradient-to-r from-yellow-400 to-primary hover:scale-[1.02] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="subscribe-yearly"
              >
                {busy === 'yearly' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                {isAuthed ? L('Choisir cette offre', 'Choose this plan') : L('Se connecter pour continuer', 'Log in to continue')}
              </button>
            </div>
          </div>

          <p className="text-center text-xs text-zinc-500 mt-6 inline-flex items-center justify-center gap-2 w-full">
            <ShieldCheck className="w-3.5 h-3.5 text-green-400" /> {L('Paiement 100% sécurisé Stripe · Résiliable en un clic depuis votre espace', '100% secure Stripe payment · Cancel anytime from your account')}
          </p>

          {/* Code promo (amis / tests) — débloque Suivi Pro sans paiement */}
          {isAuthed && (
            <div className="max-w-md mx-auto mt-8 text-center" data-testid="sub-promo-block">
              <p className="text-zinc-500 text-sm mb-3">{L('Vous avez un code promo ?', 'Got a promo code?')}</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promo}
                  onChange={(e) => { setPromo(e.target.value); setPromoErr(''); }}
                  placeholder={L('Code promo', 'Promo code')}
                  className="flex-1 bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:border-primary focus:outline-none uppercase"
                  data-testid="sub-promo-input"
                />
                <button
                  onClick={redeemPromo}
                  disabled={promoBusy || !promo.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-primary to-pink-500 text-white font-bold rounded-xl transition-transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 flex items-center gap-2"
                  data-testid="sub-promo-apply-btn"
                >
                  {promoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {L('Débloquer', 'Unlock')}
                </button>
              </div>
              {promoErr && <p className="text-red-400 text-sm mt-2" data-testid="sub-promo-error">{promoErr}</p>}
            </div>
          )}
          {!isAuthed && !loading && (
            <p className="text-center text-sm text-zinc-400 mt-4">
              <Lock className="w-3.5 h-3.5 inline mr-1" />
              {L('Vous avez déjà un compte ?', 'Already have an account?')}{' '}
              <Link to="/espace-client" className="text-primary font-semibold hover:underline">{L('Connectez-vous', 'Log in')}</Link>
            </p>
          )}
        </div>
      </main>
    </>
  );
};

export default SuiviProPage;
